// Publishing: a version of the page as one static HTML file, its styles
// inside it. It reads in full with scripts off; only a page with a
// component that has behaviour carries a script, the islands bundle,
// which picks the components up where the HTML left them.
// meets: publish/static
import { h, render } from "preact";
import { componentCss, components } from "./components";
import { css, type Rule } from "./style";
import { MadeContext, PageView, defsOf } from "./render";
import type { NodeJSON } from "./doc";

declare const ISLANDS: string; // the islands bundle's text, put in at build time

export function rulesOf(nodes: NodeJSON[]): Rule[] {
  const rules: Rule[] = [];
  const walk = (n: NodeJSON) => (Object.keys(n.style).length && rules.push({ id: n.id, style: n.style }), n.children.forEach(walk));
  nodes.forEach(walk);
  return rules;
}

function behaves(nodes: NodeJSON[], defs: Record<string, NodeJSON>, seen = new Set<string>()): boolean {
  return nodes.some((n) => {
    if (components[n.type]?.behaviour) return true;
    const d = defs[n.type];
    if (d && !seen.has(n.type)) return seen.add(n.type), behaves(d.children, defs, seen);
    return behaves(n.children, defs, seen);
  });
}

const BASE = `
:root{color-scheme:light dark}
body{margin:0;background:light-dark(#fbfaf7,#16181d);font:17px/1.6 system-ui,sans-serif}
.page{max-width:52rem;margin:0 auto;padding:48px 20px;color:var(--ink)}
.page a{color:var(--accent)}
.page .tx{display:block;white-space:pre-wrap;word-wrap:break-word}
.missing{border:1px dashed var(--muted);border-radius:6px;padding:8px 12px;margin:.6em 0}
.missing-why{display:block;font-size:13px;color:var(--muted)}
`;

export function publish(nodes: NodeJSON[], defs: NodeJSON[], tokens: Record<string, string>, title: string): string {
  const made = defsOf(defs);
  const host = document.createElement("div");
  render(h(MadeContext.Provider, { value: { defs: made, within: [] } }, h(PageView, { nodes })), host);
  const body = host.innerHTML;
  render(null, host);
  const all = [...nodes, ...defs];
  const style = [BASE, componentCss(".page"), css(rulesOf(all), tokens)].join("\n");
  const script = behaves(nodes, made)
    ? `<script type="application/json" id="page-data">${JSON.stringify({ nodes, defs }).replace(/</g, "\\u003c")}</script>\n<script type="module">${ISLANDS.replace(/<\/script/gi, "<\\/script")}</script>\n`
    : "";
  const esc = (s: string) => s.replace(/[&<>"]/g, (c) => `&#${c.charCodeAt(0)};`);
  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${esc(title)}</title>
<style>${style.replace(/<\//g, "<\\/")}</style>
</head>
<body>
<main class="page">${body}</main>
${script}</body>
</html>
`;
}
